import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

export type ContactMessage = {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  atelier_name?: string | null;
  message?: string | null;
  source?: string | null;
  status?: string | null;
  is_read: boolean;
  read_at?: string | null;
  created_at?: string | null;
};

export async function fetchContactMessages(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  unread?: boolean;
}): Promise<
  | { ok: true; data: ContactMessage[]; meta: PaginationMeta }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.per_page) q.set('per_page', String(params.per_page));
  if (params?.search) q.set('search', params.search);
  if (params?.unread === true) q.set('unread', '1');
  if (params?.unread === false) q.set('unread', '0');
  const qs = q.toString();
  const res = await apiFetch(`/platform/contact-messages${qs ? `?${qs}` : ''}`);
  const out = await parseJsonResponse<ContactMessage[]>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, data: out.data ?? [], meta: out.meta as PaginationMeta };
}

export async function fetchContactMessagesUnreadCount(): Promise<
  { ok: true; unread_count: number } | { ok: false; message: string }
> {
  const res = await apiFetch('/platform/contact-messages/unread-count');
  const out = await parseJsonResponse<{ unread_count: number }>(res);
  if (out.ok === false) return { ok: false, message: out.message };
  return { ok: true, unread_count: Number(out.data?.unread_count ?? 0) };
}

export async function openContactMessage(id: number): Promise<
  { ok: true; data: ContactMessage } | { ok: false; message: string }
> {
  const res = await apiFetch(`/platform/contact-messages/${id}`);
  const out = await parseJsonResponse<ContactMessage>(res);
  if (out.ok === false) return { ok: false, message: out.message };
  return { ok: true, data: out.data as ContactMessage };
}

export async function markContactMessageRead(id: number): Promise<
  { ok: true; data: ContactMessage } | { ok: false; message: string }
> {
  const res = await apiFetch(`/platform/contact-messages/${id}/read`, { method: 'POST' });
  const out = await parseJsonResponse<ContactMessage>(res);
  if (out.ok === false) return { ok: false, message: out.message };
  return { ok: true, data: out.data as ContactMessage };
}

export async function markAllContactMessagesRead(): Promise<
  { ok: true; updated: number } | { ok: false; message: string }
> {
  const res = await apiFetch('/platform/contact-messages/read-all', { method: 'POST' });
  const out = await parseJsonResponse<{ updated: number }>(res);
  if (out.ok === false) return { ok: false, message: out.message };
  return { ok: true, updated: Number(out.data?.updated ?? 0) };
}
